import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ChevronRight, ChevronLeft, CheckCircle } from 'lucide-react';

const UserSurvey = ({ onComplete }) => {
 const [currentStep, setCurrentStep] = useState(0);
 const [surveyData, setSurveyData] = useState({
 // Contact info (new step)
 first_name: '',
 last_name: '',
 phone: '',
 contact_email: '',

 // User Type
 userType: '', // 'home' or 'business'
 businessType: '', // if business
 businessSize: '', // if business
 
 // Machine Information
 hasMachine: '',
 machineType: [],
 machineCount: 1,
 plannedPurchase: '',
 
 // Interest & Goals
 primaryInterest: [],
 businessGoals: [], // if business
 experienceLevel: '',
 
 // Demographics
 region: '',
 ageRange: '',
 
 // Content Preferences
 contentPreference: [],
 learningStyle: ''
 });

 const steps = [
 {
 id: 'contact',
 title: 'Tell us your name and how to reach you',
 description: 'We\'ll use this for account updates and any support you request. Nothing is shared without your permission.'
 },
 {
 id: 'user-type',
 title: 'Tell us about yourself',
 description: 'This helps us personalize your experience'
 },
 {
 id: 'machine-info',
 title: 'Your freeze dryer setup',
 description: 'Help us recommend the right content and products'
 },
 {
 id: 'interests',
 title: 'What interests you most?',
 description: 'Customize your content feed'
 },
 {
 id: 'demographics',
 title: 'A bit more about you',
 description: 'Optional but helps us serve you better'
 },
 {
 id: 'preferences',
 title: 'Content preferences',
 description: 'How do you prefer to learn?'
 }
 ];

 const handleNext = () => {
 if (currentStep < steps.length - 1) {
 setCurrentStep(currentStep + 1);
 } else {
 // Complete survey
 onComplete(surveyData);
 }
 };

 const handleBack = () => {
 if (currentStep > 0) {
 setCurrentStep(currentStep - 1);
 }
 };

 const updateSurveyData = (field, value) => {
 setSurveyData(prev => ({ ...prev, [field]: value }));
 };

 const toggleArrayValue = (field, value) => {
 setSurveyData(prev => ({
 ...prev,
 [field]: prev[field].includes(value)
 ? prev[field].filter(v => v !== value)
 : [...prev[field], value]
 }));
 };

 const renderStep = () => {
 switch (steps[currentStep].id) {
 case 'contact':
 return (
 <div className="space-y-5" data-testid="survey-contact-step">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="space-y-1.5">
 <Label htmlFor="contact-first-name" className="text-sm font-semibold">First name</Label>
 <Input
 id="contact-first-name"
 data-testid="contact-first-name"
 value={surveyData.first_name}
 onChange={(e) => updateSurveyData('first_name', e.target.value)}
 placeholder="Alex"
 autoComplete="given-name"
 />
 </div>
 <div className="space-y-1.5">
 <Label htmlFor="contact-last-name" className="text-sm font-semibold">Last name</Label>
 <Input
 id="contact-last-name"
 data-testid="contact-last-name"
 value={surveyData.last_name}
 onChange={(e) => updateSurveyData('last_name', e.target.value)}
 placeholder="Rivera"
 autoComplete="family-name"
 />
 </div>
 </div>
 <div className="space-y-1.5">
 <Label htmlFor="contact-phone" className="text-sm font-semibold">Phone <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
 <Input
 id="contact-phone"
 data-testid="contact-phone"
 type="tel"
 value={surveyData.phone}
 onChange={(e) => updateSurveyData('phone', e.target.value)}
 placeholder="(555) 555-0100"
 autoComplete="tel"
 />
 </div>
 <div className="space-y-1.5">
 <Label htmlFor="contact-email" className="text-sm font-semibold">Preferred contact email <span className="text-muted-foreground text-xs font-normal">(if different from login)</span></Label>
 <Input
 id="contact-email"
 data-testid="contact-email"
 type="email"
 value={surveyData.contact_email}
 onChange={(e) => updateSurveyData('contact_email', e.target.value)}
 placeholder="you@yourbusiness.com"
 autoComplete="email"
 />
 </div>
 <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 text-xs text-foreground/70">
 Only your first and last name are shown to other members. We never share your phone or contact email.
 </div>
 </div>
 );

 case 'user-type':
 return (
 <div className="space-y-6">
 <div>
 <Label className="text-lg font-semibold mb-4 block">Are you using freeze drying for?</Label>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <Card 
 className={`cursor-pointer transition-all hover:shadow-lg ${surveyData.userType === 'home' ? 'ring-2 ring-blue-500' : ''}`}
 onClick={() => updateSurveyData('userType', 'home')}
 >
 <CardContent className="p-6 text-center">
 <div className="text-4xl mb-3">🏠</div>
 <h3 className="font-bold text-lg">Home Use</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400 mt-2">
 Personal preservation, family meals, emergency prep
 </p>
 </CardContent>
 </Card>
 
 <Card 
 className={`cursor-pointer transition-all hover:shadow-lg ${surveyData.userType === 'business' ? 'ring-2 ring-blue-500' : ''}`}
 onClick={() => updateSurveyData('userType', 'business')}
 >
 <CardContent className="p-6 text-center">
 <div className="text-4xl mb-3">🏢</div>
 <h3 className="font-bold text-lg">Business</h3>
 <p className="text-sm text-slate-600 dark:text-neutral-400 mt-2">
 Commercial production, sales, services
 </p>
 </CardContent>
 </Card>
 </div>
 </div>

 {surveyData.userType === 'business' && (
 <div className="space-y-4 animate-fadeIn">
 <div>
 <Label>What type of business?</Label>
 <select
 value={surveyData.businessType}
 onChange={(e) => updateSurveyData('businessType', e.target.value)}
 className="w-full p-2 border rounded-lg mt-1"
 >
 <option value="">Select...</option>
 <option value="food-production">Food Production/Sales</option>
 <option value="meal-prep">Meal Prep Service</option>
 <option value="pet-treats">Pet Treats</option>
 <option value="emergency-food">Emergency Food Supply</option>
 <option value="ingredients">Ingredient Supply</option>
 <option value="education">Education/Training</option>
 <option value="other">Other</option>
 </select>
 </div>
 <div>
 <Label>Business size</Label>
 <select
 value={surveyData.businessSize}
 onChange={(e) => updateSurveyData('businessSize', e.target.value)}
 className="w-full p-2 border rounded-lg mt-1"
 >
 <option value="">Select...</option>
 <option value="startup">Just Starting</option>
 <option value="small">Small (1-5 machines)</option>
 <option value="medium">Medium (6-20 machines)</option>
 <option value="large">Large (20+ machines)</option>
 </select>
 </div>
 </div>
 )}
 </div>
 );

 case 'machine-info':
 return (
 <div className="space-y-6">
 <div>
 <Label className="text-lg font-semibold mb-4 block">Do you currently own a freeze dryer?</Label>
 <div className="flex gap-4">
 <Button
 variant={surveyData.hasMachine === 'yes' ? 'default' : 'outline'}
 onClick={() => updateSurveyData('hasMachine', 'yes')}
 className="flex-1"
 >
 Yes, I have one
 </Button>
 <Button
 variant={surveyData.hasMachine === 'no' ? 'default' : 'outline'}
 onClick={() => updateSurveyData('hasMachine', 'no')}
 className="flex-1"
 >
 Not yet
 </Button>
 </div>
 </div>

 {surveyData.hasMachine === 'yes' && (
 <div className="space-y-4 animate-fadeIn">
 <div>
 <Label>Which machine(s) do you have? (Select all that apply)</Label>
 <div className="grid grid-cols-2 gap-3 mt-2">
 {['Small Freeze Dryer', 'Medium Freeze Dryer', 'Large Freeze Dryer', 'Commercial Freeze Dryer', 'Industrial Freeze Dryer', 'Other'].map(machine => (
 <Button
 key={machine}
 variant={surveyData.machineType.includes(machine) ? 'default' : 'outline'}
 onClick={() => toggleArrayValue('machineType', machine)}
 className="justify-start"
 >
 {machine}
 </Button>
 ))}
 </div>
 </div>
 <div>
 <Label>How many machines?</Label>
 <Input
 type="number"
 min="1"
 value={surveyData.machineCount}
 onChange={(e) => updateSurveyData('machineCount', parseInt(e.target.value))}
 className="mt-1"
 />
 </div>
 </div>
 )}

 {surveyData.hasMachine === 'no' && (
 <div className="animate-fadeIn">
 <Label>Planning to purchase?</Label>
 <select
 value={surveyData.plannedPurchase}
 onChange={(e) => updateSurveyData('plannedPurchase', e.target.value)}
 className="w-full p-2 border rounded-lg mt-1"
 >
 <option value="">Select timeframe...</option>
 <option value="immediate">Within 1 month</option>
 <option value="soon">1-3 months</option>
 <option value="exploring">3-6 months</option>
 <option value="researching">Just researching</option>
 </select>
 </div>
 )}
 </div>
 );

 case 'interests':
 return (
 <div className="space-y-6">
 <div>
 <Label className="text-lg font-semibold mb-4 block">What do you want to freeze dry? (Select all)</Label>
 <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
 {[
 { value: 'fruits', label: '🍎 Fruits', emoji: '🍎' },
 { value: 'vegetables', label: '🥕 Vegetables', emoji: '🥕' },
 { value: 'meats', label: '🥩 Meats', emoji: '🥩' },
 { value: 'meals', label: '🍲 Complete Meals', emoji: '🍲' },
 { value: 'desserts', label: '🍰 Desserts', emoji: '🍰' },
 { value: 'dairy', label: '🧀 Dairy', emoji: '🧀' },
 { value: 'herbs', label: '🌿 Herbs & Spices', emoji: '🌿' },
 { value: 'pet-food', label: '🐕 Pet Food', emoji: '🐕' },
 { value: 'candy', label: '🍬 Candy', emoji: '🍬' }
 ].map(item => (
 <Button
 key={item.value}
 variant={surveyData.primaryInterest.includes(item.value) ? 'default' : 'outline'}
 onClick={() => toggleArrayValue('primaryInterest', item.value)}
 className="justify-start h-auto py-3"
 >
 <span className="text-lg mr-2">{item.emoji}</span>
 {item.label.replace(item.emoji + ' ', '')}
 </Button>
 ))}
 </div>
 </div>

 <div>
 <Label>Experience Level</Label>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
 {[
 { value: 'beginner', label: 'Beginner', desc: 'New to freeze drying' },
 { value: 'intermediate', label: 'Intermediate', desc: 'Some experience' },
 { value: 'advanced', label: 'Advanced', desc: 'Experienced user' }
 ].map(level => (
 <Card
 key={level.value}
 className={`cursor-pointer transition-all ${surveyData.experienceLevel === level.value ? 'ring-2 ring-blue-500' : ''}`}
 onClick={() => updateSurveyData('experienceLevel', level.value)}
 >
 <CardContent className="p-4 text-center">
 <h4 className="font-semibold">{level.label}</h4>
 <p className="text-xs text-slate-600 dark:text-neutral-400">{level.desc}</p>
 </CardContent>
 </Card>
 ))}
 </div>
 </div>

 {surveyData.userType === 'business' && (
 <div className="animate-fadeIn">
 <Label>Business Goals (Select all that apply)</Label>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
 {[
 'Increase production capacity',
 'Improve product quality',
 'Expand product line',
 'Reduce costs',
 'Learn new techniques',
 'Marketing & sales',
 'Regulatory compliance'
 ].map(goal => (
 <Button
 key={goal}
 variant={surveyData.businessGoals.includes(goal) ? 'default' : 'outline'}
 onClick={() => toggleArrayValue('businessGoals', goal)}
 className="justify-start"
 >
 {goal}
 </Button>
 ))}
 </div>
 </div>
 )}
 </div>
 );

 case 'demographics':
 return (
 <div className="space-y-6">
 <div>
 <Label>Region</Label>
 <select
 value={surveyData.region}
 onChange={(e) => updateSurveyData('region', e.target.value)}
 className="w-full p-2 border rounded-lg mt-1"
 >
 <option value="">Select...</option>
 <option value="north-america">North America</option>
 <option value="europe">Europe</option>
 <option value="asia">Asia</option>
 <option value="australia">Australia/NZ</option>
 <option value="south-america">South America</option>
 <option value="africa">Africa</option>
 <option value="other">Other</option>
 </select>
 </div>

 <div>
 <Label>Age Range (Optional)</Label>
 <select
 value={surveyData.ageRange}
 onChange={(e) => updateSurveyData('ageRange', e.target.value)}
 className="w-full p-2 border rounded-lg mt-1"
 >
 <option value="">Prefer not to say</option>
 <option value="18-24">18-24</option>
 <option value="25-34">25-34</option>
 <option value="35-44">35-44</option>
 <option value="45-54">45-54</option>
 <option value="55-64">55-64</option>
 <option value="65+">65+</option>
 </select>
 </div>
 </div>
 );

 case 'preferences':
 return (
 <div className="space-y-6">
 <div>
 <Label className="text-lg font-semibold mb-4 block">What content are you interested in?</Label>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 {[
 'Video tutorials',
 'Written guides',
 'Recipe collections',
 'Technical specifications',
 'Business strategies',
 'Community discussions',
 'Product recommendations',
 'Live workshops'
 ].map(content => (
 <Button
 key={content}
 variant={surveyData.contentPreference.includes(content) ? 'default' : 'outline'}
 onClick={() => toggleArrayValue('contentPreference', content)}
 className="justify-start"
 >
 {content}
 </Button>
 ))}
 </div>
 </div>

 <div>
 <Label>Learning Style</Label>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
 {[
 { value: 'visual', label: '👁️ Visual', desc: 'Videos & images' },
 { value: 'reading', label: '📚 Reading', desc: 'Articles & docs' },
 { value: 'hands-on', label: '🔧 Hands-on', desc: 'Interactive & practical' }
 ].map(style => (
 <Card
 key={style.value}
 className={`cursor-pointer transition-all ${surveyData.learningStyle === style.value ? 'ring-2 ring-blue-500' : ''}`}
 onClick={() => updateSurveyData('learningStyle', style.value)}
 >
 <CardContent className="p-4 text-center">
 <div className="text-2xl mb-2">{style.label.split(' ')[0]}</div>
 <h4 className="font-semibold">{style.label.split(' ')[1]}</h4>
 <p className="text-xs text-slate-600 dark:text-neutral-400">{style.desc}</p>
 </CardContent>
 </Card>
 ))}
 </div>
 </div>
 </div>
 );

 default:
 return null;
 }
 };

 return (
 <div className="max-w-4xl mx-auto p-6">
 {/* Progress Bar — percentage on the right, current step title on the left */}
 <div className="mb-8" data-testid="survey-progress">
 <div className="flex items-baseline justify-between mb-2 gap-4">
 <div className="min-w-0 flex-1">
 <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
 Step {currentStep + 1} of {steps.length}
 </div>
 <div className="text-sm font-heading font-extrabold truncate text-foreground" data-testid="survey-progress-title">
 {steps[currentStep].title}
 </div>
 </div>
 <div className="text-primary text-sm font-heading font-extrabold tabular-nums" data-testid="survey-progress-percent">
 {Math.round(((currentStep + 1) / steps.length) * 100)}%
 </div>
 </div>
 <div className="h-1.5 bg-muted rounded-full overflow-hidden">
 <div
 className="h-full bg-primary transition-all duration-300"
 style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
 />
 </div>
 </div>

 {/* Survey Card */}
 <Card>
 <CardHeader>
 <CardTitle>{steps[currentStep].title}</CardTitle>
 <CardDescription>{steps[currentStep].description}</CardDescription>
 </CardHeader>
 <CardContent className="min-h-[400px]">
 {renderStep()}
 </CardContent>
 </Card>

 {/* Navigation */}
 <div className="flex justify-between mt-6">
 <Button
 variant="outline"
 onClick={handleBack}
 disabled={currentStep === 0}
 >
 <ChevronLeft className="w-4 h-4 mr-2" />
 Back
 </Button>
 <div className="flex gap-3">
 <Button
 variant="ghost"
 onClick={() => {
 localStorage.setItem('survey_skipped', 'true');
 window.location.href = '/dashboard';
 }}
 className="text-slate-500"
 >
 Skip for now
 </Button>
 <Button onClick={handleNext}>
 {currentStep === steps.length - 1 ? (
 <>
 <CheckCircle className="w-4 h-4 mr-2" />
 Complete Setup
 </>
 ) : (
 <>
 Next
 <ChevronRight className="w-4 h-4 ml-2" />
 </>
 )}
 </Button>
 </div>
 </div>
 </div>
 );
};

export default UserSurvey;
